import { supabase } from '@/shared/lib/supabase';
import { applyWindow, probeLimit, type ListWindow } from '@/shared/lib/listWindow';

/**
 * Audit-log read service. Reads `audit_log` directly via RLS:
 * staff get all rows; customers get their own (public, non-system).
 *
 * `audit_log` is append-only (immutable trigger at the DB level), so this
 * module only exposes reads.
 */

export type AuditActorKind = 'customer' | 'staff' | 'system';

export interface AuditLogEntry {
  id: string;
  customer_id: string | null;
  customer_name: string | null;
  customer_email: string | null;
  actor_id: string | null;
  actor_kind: AuditActorKind;
  actor_email: string | null;
  event: string;
  entity_kind: string | null;
  entity_id: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  user_agent: string | null;
  request_id: string | null;
  created_at: string;
}

/**
 * Actor scope for the list.
 *
 * `'people'` is not an `actor_kind` value — it is "anything a human did",
 * i.e. everything except `system`. It exists because **94% of `audit_log` is
 * system-generated** (10,241 of 10,904 rows, measured 2026-09-13):
 * domain_link.update 33%, fulfilment_exception.run 15%,
 * phase10_studio_slo.run 15%, the two cron_watchdog jobs 7% each — against
 * invoice.state_transition at 1.1%.
 *
 * The effect on what the page can actually reach, at a cap of 200:
 *   all actors → 4,722 rows/30d → about 1.3 DAYS of history
 *   people     →   193 rows/30d → about 1.04 MONTHS
 * A filter default is worth 24x more here than the period control is.
 */
export type AuditActorScope = AuditActorKind | 'all' | 'people';

export interface AuditListFilter {
  customer_id?: string;
  event_kind?: string;
  /** Accepts a single kind, `'all'`, or `'people'` (everything but `system`). */
  actor_kind?: AuditActorScope;
  since?: string;
  until?: string;
  limit?: number;
}

interface AuditRow {
  id: string;
  customer_id: string | null;
  actor_id: string | null;
  actor_kind: AuditActorKind;
  event: string;
  entity_kind: string | null;
  entity_id: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  user_agent: string | null;
  request_id: string | null;
  created_at: string;
}

interface CustomerNameRow {
  customer_id: string;
  full_name: string;
  email: string | null;
}

const COLS =
  'id, customer_id, actor_id, actor_kind, event, entity_kind, entity_id, before, after, ip, user_agent, request_id, created_at';

export const auditService = {
  /**
   * List audit log entries with optional filters. Staff-only via RLS at
   * the table level. Returns up to `limit` (default 200) rows ordered by
   * created_at DESC. Joins `customer_profile` to denormalise name + email.
   */
  async list(filter: AuditListFilter = {}): Promise<ListWindow<AuditLogEntry>> {
    const limit = filter.limit ?? 200;
    let q = supabase
      .from('audit_log')
      .select(COLS)
      .order('created_at', { ascending: false })
      // AR-6: ask for one more than we will show, so the caller can tell
      // "there were exactly 200" from "there were ten thousand". Measured
      // 2026-09-13: 4,722 rows land here every 30 days against a cap of 200,
      // so this page sees roughly 1.3 DAYS unless it is date-bounded. It has
      // truncated since the day it shipped and has never once said so.
      .limit(probeLimit(limit));

    if (filter.customer_id) q = q.eq('customer_id', filter.customer_id);
    if (filter.event_kind) q = q.eq('event', filter.event_kind);
    if (filter.actor_kind === 'people') q = q.neq('actor_kind', 'system');
    else if (filter.actor_kind && filter.actor_kind !== 'all') {
      q = q.eq('actor_kind', filter.actor_kind);
    }
    if (filter.since) q = q.gte('created_at', filter.since);
    if (filter.until) q = q.lte('created_at', filter.until);

    const { data, error } = await q;
    if (error) {
      throw new Error(`Could not load audit log: ${error.message}`);
    }
    // Trim to `limit` BEFORE the name lookup, so the AR-6 probe row never costs
    // an extra id in the `in(...)` filter.
    const win = applyWindow((data ?? []) as AuditRow[], limit);
    const rows = win.rows;

    // Denormalise customer_name + customer_email — single follow-up query
    // for the unique customer_ids in the page (RLS-scoped).
    const customerIds = Array.from(
      new Set(rows.map((r) => r.customer_id).filter((id): id is string => !!id))
    );

    const nameByCustomer = new Map<string, { name: string; email: string | null }>();
    if (customerIds.length > 0) {
      const { data: summaryRows, error: summaryErr } = await supabase
        .from('v_admin_customer_summary')
        .select('customer_id, full_name, email')
        .in('customer_id', customerIds);
      if (!summaryErr && summaryRows) {
        for (const s of summaryRows as CustomerNameRow[]) {
          nameByCustomer.set(s.customer_id, {
            name: s.full_name,
            email: s.email,
          });
        }
      }
    }

    const mapped = rows.map((r) => {
      const denorm = r.customer_id ? nameByCustomer.get(r.customer_id) : undefined;
      return {
        id: r.id,
        customer_id: r.customer_id,
        customer_name: denorm?.name ?? null,
        customer_email: denorm?.email ?? null,
        actor_id: r.actor_id,
        actor_kind: r.actor_kind,
        actor_email: null,
        event: r.event,
        entity_kind: r.entity_kind,
        entity_id: r.entity_id,
        before: r.before,
        after: r.after,
        ip: r.ip,
        user_agent: r.user_agent,
        request_id: r.request_id,
        created_at: r.created_at,
      };
    });

    return { ...win, rows: mapped };
  },
};
